import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { AppDispatch, RootState } from '../../../Redux/Store';
import {
  createReservationMenu,
  deleteReservationMenu,
  fetchReservationMenus,
  fetchReservationSetting,
  updateReservationMenu,
} from '../../../Redux/ReservationSettings/reservationSettingsApi';
import {
  calcPriceIncludingTax,
  DEFAULT_TAX_RATE_PERCENT,
  ReservationMenu,
} from '../../../types/reservation';
import ConfirmDialog from '../../../Components/Dialog/ConfirmDialog';
import NumberField from '../../../Components/NumberInput/NumberField';
import ReservationImageUploader from './ReservationImageUploader';
import { SavedSnackbar, TabDescription, useSaveFeedback } from './SettingsShared';

interface Props {
  storeId: string;
}

/** コース画像の最大枚数（契約B・API バリデーションと揃える） */
const MAX_COURSE_IMAGES = 3;

/** 追加/編集ダイアログのフォーム状態 */
interface CourseForm {
  name: string;
  price: number;
  description: string;
  /** ON なら stayMinutes=null（受付設定のデフォルト滞在時間を使う） */
  useDefaultStay: boolean;
  stayMinutes: number;
  /** コース画像 URL（並び順=表示順・最大3枚） */
  imageUrls: string[];
}

const emptyForm: CourseForm = {
  name: '',
  price: 0,
  description: '',
  useDefaultStay: true,
  stayMinutes: 120,
  imageUrls: [],
};

/**
 * タブ⑤コース。予約時に選択できるコースメニューの CRUD。
 * stayMinutes=null のコースは受付設定のデフォルト滞在時間が適用される。
 * コース画像（最大3枚）はアップロード後、コース保存時に imageUrls として確定する（フィードバック2）。
 */
const CoursesTab: React.FC<Props> = ({ storeId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const menus = useSelector((state: RootState) => state.reservationSettings.menus);
  // 税込プレビュー算出用の税率（受付設定から取得。未設定は既定10%）
  const taxRatePercent = useSelector(
    (state: RootState) =>
      state.reservationSettings.setting?.taxRatePercent ?? DEFAULT_TAX_RATE_PERCENT,
  );

  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ReservationMenu | null>(null);
  const [form, setForm] = useState<CourseForm>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReservationMenu | null>(null);
  const { savedOpen, closeSaved, showSaved, error, setError, showError } = useSaveFeedback();

  useEffect(() => {
    // 税込プレビューに使う税率を受付設定から取得する（税込 = floor(税抜 × (1 + 税率/100))）
    dispatch(fetchReservationSetting(storeId));
    dispatch(fetchReservationMenus(storeId))
      .unwrap()
      .catch(showError)
      .finally(() => setLoaded(true));
  }, [dispatch, storeId, showError]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (menu: ReservationMenu) => {
    setEditing(menu);
    setForm({
      name: menu.name,
      price: menu.price,
      description: menu.description ?? '',
      useDefaultStay: menu.stayMinutes == null,
      stayMinutes: menu.stayMinutes ?? 120,
      imageUrls: menu.imageUrls ?? [],
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const submit = () => {
    if (!form.name.trim()) {
      setFormError('コース名を入力してください。');
      return;
    }
    if (!form.useDefaultStay && form.stayMinutes < 1) {
      setFormError('滞在時間は 1 分以上を入力してください。');
      return;
    }
    // sortIndex は新規なら末尾（既存最大+1）、編集なら現状維持
    const sortIndex = editing
      ? editing.sortIndex
      : menus.length > 0
        ? Math.max(...menus.map((m) => m.sortIndex)) + 1
        : 0;
    const input = {
      name: form.name.trim(),
      price: form.price,
      description: form.description.trim() ? form.description.trim() : null,
      sortIndex,
      stayMinutes: form.useDefaultStay ? null : form.stayMinutes,
      imageUrls: form.imageUrls,
    };
    setBusy(true);
    const action = editing
      ? dispatch(updateReservationMenu({ storeId, menuId: editing.id, input }))
      : dispatch(createReservationMenu({ storeId, input }));
    action
      .unwrap()
      .then(() => {
        setDialogOpen(false);
        showSaved();
      })
      .catch(showError)
      .finally(() => setBusy(false));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    setBusy(true);
    dispatch(deleteReservationMenu({ storeId, menuId: deleteTarget.id }))
      .unwrap()
      .then(() => showSaved())
      .catch(showError)
      .finally(() => {
        setDeleteTarget(null);
        setBusy(false);
      });
  };

  if (!loaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <TabDescription text="お客様が予約時に選べるコースです。滞在時間を設定するとそのコースの予約に適用されます" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreate}>
          コースを追加
        </Button>
      </Box>

      {menus.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
          コースはまだ登録されていません。
        </Typography>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>画像</TableCell>
                <TableCell>名前</TableCell>
                <TableCell>価格（税抜）</TableCell>
                <TableCell>説明</TableCell>
                <TableCell>滞在時間</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {menus.map((menu) => (
                <TableRow key={menu.id}>
                  <TableCell sx={{ width: 56 }}>
                    {menu.imageUrls && menu.imageUrls.length > 0 ? (
                      <Box
                        component="img"
                        src={menu.imageUrls[0]}
                        alt={menu.name}
                        sx={{
                          width: 48,
                          height: 36,
                          objectFit: 'cover',
                          borderRadius: 0.5,
                          display: 'block',
                        }}
                      />
                    ) : null}
                  </TableCell>
                  <TableCell>{menu.name}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {menu.price.toLocaleString()}円
                  </TableCell>
                  <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {menu.description ?? ''}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {menu.stayMinutes == null ? '店舗デフォルト' : `${menu.stayMinutes}分`}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <IconButton size="small" onClick={() => openEdit(menu)} aria-label="編集">
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => setDeleteTarget(menu)}
                      aria-label="削除"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 追加/編集ダイアログ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? 'コースを編集' : 'コースを追加'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {formError}
            </Alert>
          )}
          <TextField
            label="コース名"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
            margin="dense"
            size="small"
            inputProps={{ maxLength: 100 }}
          />
          <NumberField
            label="価格（税抜・円）"
            value={form.price}
            onChangeValue={(price) => setForm({ ...form, price })}
            min={0}
            helperText={`税込 ¥${calcPriceIncludingTax(
              form.price,
              taxRatePercent,
            ).toLocaleString()}（税率${taxRatePercent}%）`}
            fullWidth
            margin="dense"
            size="small"
          />
          <TextField
            label="説明（任意）"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            multiline
            minRows={2}
            fullWidth
            margin="dense"
            size="small"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.useDefaultStay}
                onChange={(e) => setForm({ ...form, useDefaultStay: e.target.checked })}
              />
            }
            label="滞在時間は店舗デフォルトを使う"
          />
          {!form.useDefaultStay && (
            <NumberField
              label="滞在時間（分）"
              value={form.stayMinutes}
              onChangeValue={(stayMinutes) => setForm({ ...form, stayMinutes })}
              min={0}
              helperText="このコースを予約したお客様は、この時間だけ席を占有します（未指定＝店舗デフォルト）"
              fullWidth
              margin="dense"
              size="small"
            />
          )}
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="subtitle2" gutterBottom>
              コース画像（最大{MAX_COURSE_IMAGES}枚）
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              予約ページのコース選択に表示されます。1枚目がサムネイルになります
            </Typography>
            <ReservationImageUploader
              storeId={storeId}
              kind="COURSE"
              urls={form.imageUrls}
              onChange={(imageUrls) => setForm({ ...form, imageUrls })}
              maxCount={MAX_COURSE_IMAGES}
              addLabel="画像を追加"
              disabled={busy}
              onError={(e) => setFormError(e instanceof Error ? e.message : String(e))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setDialogOpen(false)} disabled={busy}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={submit} disabled={busy}>
            {busy ? <CircularProgress size={22} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認 */}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="コースの削除"
        message={`「${deleteTarget?.name ?? ''}」を削除します。よろしいですか？`}
        confirmText="削除する"
        confirmColor="error"
        closeColor="inherit"
      />

      <SavedSnackbar open={savedOpen} onClose={closeSaved} />
    </Box>
  );
};

export default CoursesTab;
